import { Button, Form, message, Modal, Spin } from 'antd'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { PropertyLabel } from '../../../common/propertyDetailTypes'
import {
  useCreateLabelConnectionMutation,
  useDeleteLabelConnectionMutation,
} from '../../../graphql/generated'
import {
  LabelBulkEditOptions,
  LabelConnectionFragment,
} from '../../utils/labelsTypes'
import { LabelEditTypeSelectInput } from '../LabelEditTypeSelectInput/LabelEditTypeSelectInput'
import { LabelSelectInput } from '../LabelSelectInput/LabelSelectInput'

export interface PropertyLabelTableBulkEditLabelProps {
  selectedRows: (PropertyLabel & { labels?: LabelConnectionFragment | null })[]
  refetch: () => void
  limit: number
}

interface FormValues {
  labelEditType: LabelBulkEditOptions
  label?: string[]
}

const initialValues = {
  labelEditType: LabelBulkEditOptions.Add,
}

export const PropertyLabelTableBulkEditLabel: FunctionComponent<PropertyLabelTableBulkEditLabelProps> =
  ({ selectedRows, refetch, limit }) => {
    const [open, setOpen] = useState(false)
    const [saving, setSaving] = useState(false)
    const [form] = Form.useForm<FormValues>()
    const [, addLabelConnection] = useCreateLabelConnectionMutation()
    const [, deleteLabelConnection] = useDeleteLabelConnectionMutation()

    const count = selectedRows?.length || 0

    useEffect(() => {
      if (count > limit) {
        message.warning(
          `Překročen limit ${limit} položek. Zpracováno bude jen prvních ${limit}.`
        )
      }
    }, [limit, count])

    const handleCancel = useCallback(() => {
      if (!saving) setOpen(false)
    }, [saving])

    const handleOpen = useCallback(() => {
      setOpen(true)
    }, [])

    const onOk = useCallback(() => {
      form.submit()
    }, [form])

    const handleFinish = useCallback(
      async (values: FormValues) => {
        if (!count) return
        setSaving(true)
        const { labelEditType, label: selectedLabelIds = [] } = values
        try {
          const rowsToProcess =
            count > limit ? selectedRows.slice(0, limit) : selectedRows

          for (const row of rowsToProcess) {
            const initialConnections = row.labels?.nodes?.filter(Boolean) || []
            const initialLabelIds = initialConnections
              .map(connection => connection?.labelByLabelId?.id)
              .filter((id): id is string => !!id)
            if (labelEditType === LabelBulkEditOptions.Inherit) continue
            if (labelEditType === LabelBulkEditOptions.Add) {
              for (const labelId of selectedLabelIds) {
                if (!initialLabelIds.includes(labelId)) {
                  await addLabelConnection({ propertyLabelId: row.id, labelId })
                }
              }
            }
            if (labelEditType === LabelBulkEditOptions.RemoveSelected) {
              for (const conn of initialConnections) {
                const labelId = conn?.labelByLabelId?.id
                if (labelId && selectedLabelIds.includes(labelId) && conn?.id) {
                  await deleteLabelConnection({ id: conn.id })
                }
              }
            }
            if (labelEditType === LabelBulkEditOptions.RemoveAll) {
              for (const connection of initialConnections) {
                if (connection?.id)
                  await deleteLabelConnection({ id: connection.id })
              }
            }
            if (labelEditType === LabelBulkEditOptions.ReplaceBy) {
              for (const conn of initialConnections) {
                const labelId = conn?.labelByLabelId?.id
                if (
                  labelId &&
                  !selectedLabelIds.includes(labelId) &&
                  conn?.id
                ) {
                  await deleteLabelConnection({ id: conn.id })
                }
              }
              for (const labelId of selectedLabelIds) {
                if (!initialLabelIds.includes(labelId)) {
                  await addLabelConnection({ propertyLabelId: row.id, labelId })
                }
              }
            }
          }
          message.success('Štítky byly upraveny.')
          setOpen(false)
          refetch()
        } catch (e) {
          message.error('Nepodařilo se uložit všechny změny štítků.')
        } finally {
          setSaving(false)
        }
      },
      [
        addLabelConnection,
        count,
        deleteLabelConnection,
        limit,
        refetch,
        selectedRows,
      ]
    )

    return (
      <>
        <Button onClick={handleOpen} disabled={count === 0} type="primary">
          Hromadně upravit štítky
        </Button>

        <Modal
          title={`Hromadná úprava štítků (${Math.min(count, limit)})`}
          open={open}
          onCancel={handleCancel}
          onOk={onOk}
          okText="Uložit"
          cancelText="Zrušit"
          confirmLoading={saving}
          destroyOnClose
          width={700}
        >
          <Form
            layout="vertical"
            form={form}
            initialValues={initialValues}
            onFinish={handleFinish}
            className="!pt-2"
          >
            <Form.Item label="Typ úpravy" name="labelEditType">
              <LabelEditTypeSelectInput
                initialValue={LabelBulkEditOptions.Add}
              />
            </Form.Item>

            <Form.Item label="Vybrané štítky" name="label">
              <LabelSelectInput />
            </Form.Item>

            {saving && (
              <div className="flex items-center space-x-2 text-gray-500">
                <Spin size="small" />
                <span>Ukládání změn...</span>
              </div>
            )}
          </Form>
        </Modal>
      </>
    )
  }
